import React, { useState } from "react";
import { Send, ExternalLink, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

/**
 * Account deletion request form.
 *
 * Posts to Web3Forms, which emails the submission to whichever address the
 * access key is registered against. The key is read from the environment so
 * it isn't baked into the source; if it's absent the component falls back to
 * the existing Google Form so the page is never a dead end.
 */

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;
const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const FALLBACK_FORM_URL = "https://forms.gle/9rtod46grdJ8QuB66";

const inputStyle = {
  width: "100%",
  height: "46px",
  padding: "0 14px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  fontSize: "0.94rem",
  color: "#0f172a",
  outline: "none",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block",
  fontFamily: "var(--font-display)",
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "#334155",
  marginBottom: "6px",
};

function Field({ label, children, required }) {
  return (
    <div style={{ textAlign: "left", marginBottom: "16px" }}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: "#ef4444" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

export default function DeleteAccountForm() {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  // Without a key there is nothing to post to — send people to the Google Form.
  if (!WEB3FORMS_KEY) {
    return (
      <>
        <a
          href={FALLBACK_FORM_URL}
          target="_blank"
          rel="noreferrer"
          className="eb-btn"
          style={{
            background: "#dc2626",
            color: "#ffffff",
            border: "1px solid #dc2626",
            width: "100%",
          }}
        >
          Open the deletion request form <ExternalLink size={16} />
        </a>
        <p style={{ marginTop: "14px", fontSize: "0.82rem", color: "#94a3b8" }}>
          The form opens in a new tab.
        </p>
      </>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    const form = e.target;
    const data = new FormData(form);
    data.append("access_key", WEB3FORMS_KEY);
    data.append("subject", "EMIBOOK — account deletion request");
    data.append("from_name", "EMIBOOK website");

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: "POST",
        body: data,
      });
      const json = await res.json();

      if (json.success) {
        setStatus("sent");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(json.message || "The request could not be submitted.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Network error — please check your connection and try again.");
    }
  };

  if (status === "sent") {
    return (
      <div
        style={{
          background: "rgba(5, 150, 105, 0.07)",
          border: "1px solid rgba(5, 150, 105, 0.25)",
          borderRadius: "14px",
          padding: "24px",
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
          textAlign: "left",
        }}
      >
        <CheckCircle2 size={22} color="#059669" style={{ flexShrink: 0, marginTop: "1px" }} />
        <div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              color: "#065f46",
              marginBottom: "4px",
            }}
          >
            Request received
          </div>
          <p style={{ color: "#047857", fontSize: "0.9rem", margin: 0, lineHeight: 1.6 }}>
            We've logged your deletion request and our team will confirm by email.
            Processing can take up to 7 working days.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Web3Forms honeypot — hidden from people, catches bots. */}
      <input
        type="checkbox"
        name="botcheck"
        tabIndex="-1"
        autoComplete="off"
        style={{ display: "none" }}
      />

      <Field label="Full name" required>
        <input style={inputStyle} type="text" name="name" required autoComplete="name" />
      </Field>

      <Field label="Registered mobile number" required>
        <input
          style={inputStyle}
          type="tel"
          name="mobile"
          required
          inputMode="tel"
          pattern="[0-9+\-\s]{7,15}"
          title="Enter the mobile number your EMIBOOK account is registered with"
          autoComplete="tel"
        />
      </Field>

      <Field label="Email address" required>
        <input style={inputStyle} type="email" name="email" required autoComplete="email" />
      </Field>

      <Field label="Reason for deletion (optional)">
        <textarea
          style={{ ...inputStyle, height: "96px", padding: "12px 14px", resize: "vertical" }}
          name="reason"
          rows={3}
        />
      </Field>

      {status === "error" && (
        <div
          style={{
            display: "flex",
            gap: "9px",
            alignItems: "flex-start",
            background: "rgba(239, 68, 68, 0.07)",
            border: "1px solid rgba(239, 68, 68, 0.25)",
            borderRadius: "10px",
            padding: "12px 14px",
            marginBottom: "16px",
            textAlign: "left",
          }}
        >
          <AlertCircle size={17} color="#dc2626" style={{ flexShrink: 0, marginTop: "1px" }} />
          <span style={{ color: "#b91c1c", fontSize: "0.85rem" }}>{errorMsg}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "sending"}
        className="eb-btn"
        style={{
          width: "100%",
          background: status === "sending" ? "#f87171" : "#dc2626",
          color: "#ffffff",
          border: "1px solid transparent",
          cursor: status === "sending" ? "wait" : "pointer",
        }}
      >
        {status === "sending" ? (
          <>
            <Loader2 size={16} className="eb-spin" /> Submitting…
          </>
        ) : (
          <>
            Submit deletion request <Send size={15} />
          </>
        )}
      </button>

      <p style={{ marginTop: "14px", fontSize: "0.8rem", color: "#94a3b8", textAlign: "left" }}>
        We only use these details to locate and remove your account.
      </p>
    </form>
  );
}
